import React, { useState, useMemo } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';
import { PRESET_CATEGORY_MAP, CURRENCY_OPTIONS } from '../categories';
import { getDaysInMonth, getFirstDayOfMonth, getDayBalance } from '../utils/calendarMath';

const FREQS = ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Quarterly', 'Semi-annually', 'Yearly'];

// Human-readable labels for the onboarding wizard only.
// The stored values (FREQS) remain unchanged everywhere else in the app.
const FREQ_DISPLAY_LABELS = {
  'Daily':          'Every day',
  'Weekly':         'Every week',
  'Biweekly':       'Every two weeks',
  'Monthly':        'Once a month',
  'Quarterly':      'Every 3 months',
  'Semi-annually':  'Every 6 months',
  'Yearly':         'Once a year',
};
const newId = () => Math.random().toString(36).substr(2, 9);

const INCOME_PRESETS = [
  'Salary', 'Freelance', 'Side Hustle', 'Part-time Job', 'Investments', 'Rental Income', 'Other',
];
const BILL_PRESETS = [
  'Rent / Mortgage', 'Car Payment', 'Car Insurance', 'Groceries', 'Coffee',
  'Netflix', 'Phone', 'Internet', 'Utilities', 'Gym', 'Other',
];

// ─── Main wizard ─────────────────────────────────────────────────────────────

export default function OnboardingScreen({ onComplete, onSkip }) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const { t, isDark, toggle } = useTheme();
  const [page, setPage] = useState(1);
  const [userName, setUserName] = useState('');
  const [balance, setBalance] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [incomeItems, setIncomeItems] = useState([]);
  const [billItems, setBillItems] = useState([]);

  const handleFinish = () => {
    const toItem = (i, type) => ({
      id: i.id,
      name: i.label === 'Other' ? (i.customName || 'Other') : i.label,
      amount: parseFloat(i.amount) || 0,
      frequency: i.frequency,
      lastOccurrence: i.lastOccurrence || null,
      type,
      category: PRESET_CATEGORY_MAP[i.label] || (type === 'income' ? 'Other Income' : 'Other Expenses'),
    });
    onComplete({
      userName: userName.trim() || 'User',
      balance: parseFloat(balance) || 0,
      currency,
      items: [
        ...incomeItems.map((i) => toItem(i, 'income')),
        ...billItems.map((i) => toItem(i, 'expense')),
      ],
    });
  };

  // Responsive page padding
  const pagePadding = isMobile ? '20px 12px 40px' : isTablet ? '24px 16px 48px' : '32px 20px 60px';
  const containerMaxWidth = isMobile ? '100%' : '660px';

  return (
    <div style={{ ...s.page, background: t.pageBg, padding: pagePadding }}>
      <button
        onClick={toggle}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{
          position: 'fixed',
          top: '14px',
          right: '14px',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: `1px solid ${t.borderFaint}`,
          background: t.card,
          color: t.textMuted,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200,
          boxShadow: t.shadow,
          padding: 0,
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '18px', lineHeight: 1 }}>
          {isDark ? 'light_mode' : 'dark_mode'}
        </span>
      </button>
      <div style={{ ...s.container, maxWidth: containerMaxWidth }}>
        <StepBar current={page} />

        {page === 1 && (
          <PageWelcome
            userName={userName}
            setUserName={setUserName}
            balance={balance}
            setBalance={setBalance}
            currency={currency}
            setCurrency={setCurrency}
            onNext={() => setPage(2)}
            onSkip={onSkip}
            isMobile={isMobile}
          />
        )}
        {page === 2 && (
          <PageDrag
            title="When Do You Get Paid?"
            subtitle="Add your income sources — amounts are optional, you can fill them in later."
            presets={INCOME_PRESETS}
            items={incomeItems}
            onItemsChange={setIncomeItems}
            dateLabel="When did you last get paid this?"
            onBack={() => setPage(1)}
            onNext={() => setPage(3)}
            isMobile={isMobile}
          />
        )}
        {page === 3 && (
          <PageDrag
            title="What Bills Do You Pay?"
            subtitle="Add your recurring expenses — amounts are optional."
            presets={BILL_PRESETS}
            items={billItems}
            onItemsChange={setBillItems}
            dateLabel="When was this last due?"
            onBack={() => setPage(2)}
            onNext={() => setPage(4)}
            isMobile={isMobile}
          />
        )}
        {page === 4 && (
          <PageOverview
            userName={userName}
            balance={balance}
            currency={currency}
            incomeItems={incomeItems}
            billItems={billItems}
            onEdit={setPage}
            onBack={() => setPage(3)}
            onFinish={handleFinish}
          />
        )}
      </div>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEP_LABELS = ['Welcome', 'Income', 'Bills', 'Overview'];

function StepBar({ current }) {
  const { t } = useTheme();
  return (
    <div style={s.stepBar}>
      {STEP_LABELS.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: done ? '#1b6d24' : active ? '#0061a4' : t.border,
                  color: done || active ? 'white' : t.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: '700',
                  transition: 'background 0.2s',
                }}
              >
                {done ? '✓' : step}
              </div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: active ? '600' : '400',
                  color: active ? '#0061a4' : done ? '#1b6d24' : t.textFaint,
                }}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: '2px',
                  background: done ? '#1b6d24' : t.border,
                  marginBottom: '18px',
                  transition: 'background 0.2s',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Page 1: Welcome ──────────────────────────────────────────────────────────

function PageWelcome({ userName, setUserName, balance, setBalance, currency, setCurrency, onNext, onSkip, isMobile }) {
  const { t } = useTheme();
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <h1 style={{ ...s.pageTitle, color: t.text }}>Welcome to Finance Planner</h1>
        <p style={{ ...s.pageSubtitle, color: t.textMuted }}>Let's get your finances set up</p>
      </div>

      <div style={{ ...s.card, background: t.card }}>
        <div style={s.fieldGroup}>
          <label style={{ ...s.label, color: t.text }}>What's your name?</label>
          <input
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="Your name..."
            style={{ ...s.textInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && onNext()}
          />
        </div>

        <div style={{ ...s.cardDivider, borderTop: `1px solid ${t.borderLight}` }} />

        <div style={s.fieldGroup}>
          <label style={{ ...s.label, color: t.text }}>How much is in your bank account right now?</label>
          <div style={{ ...s.amountWrapper, border: `1px solid ${t.borderFaint}`, background: t.inputBg }}>
            <span style={{ ...s.dollar, background: t.pageBg, color: t.textMuted, borderRight: `1px solid ${t.borderFaint}` }}>$</span>
            <input
              type="number"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              placeholder="0.00"
              style={{ ...s.amountInput, width: '180px', background: t.inputBg, color: t.text }}
              min="0"
              step="0.01"
              onKeyDown={(e) => e.key === 'Enter' && onNext()}
            />
          </div>
          <p style={{ ...s.hint, color: t.textMuted }}>
            Finance Planner projects your future balance from this starting point
          </p>
        </div>

        <div style={{ ...s.cardDivider, borderTop: `1px solid ${t.borderLight}` }} />

        <div style={s.fieldGroup}>
          <label style={{ ...s.label, color: t.text }}>Currency</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            style={{ ...s.freqSelect, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text, width: '100%', padding: '11px 14px' }}
          >
            {CURRENCY_OPTIONS.map(c => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          <p style={{ ...s.hint, color: t.textMuted }}>Display-only — used to format amounts throughout the app</p>
        </div>
      </div>

      <hr style={{ ...s.divider, borderTop: `1px solid ${t.border}` }} />

      <div style={s.navRow}>
        <button style={s.skipBtn} onClick={onSkip}>
          Skip Setup
        </button>
        <button style={s.nextBtn} onClick={onNext}>
          Next →
        </button>
      </div>
    </div>
  );
}

// ─── Pages 2 & 3: Drag-and-drop ───────────────────────────────────────────────

const TODAY = new Date().toISOString().split('T')[0];

function PageDrag({ title, subtitle, presets, items, onItemsChange, dateLabel, onBack, onNext, isMobile }) {
  const { t } = useTheme();
  const [dragCount, setDragCount] = useState(0);

  const isAdded = (preset) =>
    preset !== 'Other' && items.some((i) => i.label === preset);

  const addItem = (preset) => {
    if (isAdded(preset)) return;
    onItemsChange([
      ...items,
      {
        id: newId(),
        label: preset,
        customName: '',
        amount: '',
        frequency: 'Monthly',
        lastOccurrence: '',
      },
    ]);
  };

  const removeItem = (id) => onItemsChange(items.filter((i) => i.id !== id));

  const updateItem = (id, key, val) =>
    onItemsChange(items.map((i) => (i.id === id ? { ...i, [key]: val } : i)));

  const dropZoneMinHeight = isMobile ? '110px' : '120px';

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ ...s.pageTitle, color: t.text }}>{title}</h2>
        <p style={{ ...s.pageSubtitle, color: t.textMuted }}>{subtitle}</p>
      </div>

      {/* Chip pool */}
      <div style={{ ...s.chipsPool, background: t.card }}>
        <p style={s.chipsLabel}>Click or drag to add:</p>
        <div style={s.chipsRow}>
          {presets.map((preset) => {
            const added = isAdded(preset);
            return (
              <button
                key={preset}
                draggable={!added}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', preset)}
                onClick={() => addItem(preset)}
                disabled={added}
                style={{
                  ...s.chip,
                  background: t.tableHeader,
                  color: t.text,
                  opacity: added ? 0.38 : 1,
                  cursor: added ? 'default' : 'grab',
                }}
              >
                {added ? '✓' : '+'} {preset}
              </button>
            );
          })}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDrop={(e) => {
          e.preventDefault();
          const preset = e.dataTransfer.getData('text/plain');
          if (preset) addItem(preset);
          setDragCount(0);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={() => setDragCount((c) => c + 1)}
        onDragLeave={() => setDragCount((c) => Math.max(0, c - 1))}
        style={{
          ...s.dropZone,
          minHeight: items.length > 0 ? 0 : dropZoneMinHeight,
          borderColor: dragCount > 0 ? '#0061a4' : 'rgba(0,97,164,0.3)',
          background: dragCount > 0 ? 'rgba(0,97,164,0.03)' : t.card,
          overflowX: 'auto',
          padding: items.length > 0 ? 0 : undefined,
        }}
      >
        {items.length === 0 ? (
          <div style={{ ...s.dropHint, minHeight: dropZoneMinHeight, color: t.textFaintest }}>
            <span style={{ fontSize: '28px' }}>⬆</span>
            <span>Click or drag items above to add them here</span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: t.tableHeader }}>
                <th style={s.tableHead}>Name</th>
                <th style={s.tableHead}>Price</th>
                <th style={s.tableHead}>Frequency</th>
                <th style={s.tableHead}>Date</th>
                <th style={{ ...s.tableHead, width: '32px' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={{ borderBottom: `1px solid ${t.borderLight}`, background: t.card }}>
                  {/* Name */}
                  <td style={s.tableCell}>
                    {item.label === 'Other' ? (
                      <input
                        type="text"
                        value={item.customName}
                        onChange={(e) => updateItem(item.id, 'customName', e.target.value)}
                        placeholder="Enter name..."
                        style={{ ...s.customNameInput, border: `1px solid ${t.borderFaint}`, color: t.text, background: t.inputBg, width: '100%' }}
                        autoFocus
                      />
                    ) : (
                      <span style={{ ...s.itemLabel, color: t.text, minWidth: 0 }}>{item.label}</span>
                    )}
                  </td>

                  {/* Price */}
                  <td style={s.tableCell}>
                    <div style={{ ...s.amountWrapper, border: `1px solid ${t.borderFaint}`, background: t.inputBg }}>
                      <span style={{ ...s.dollar, background: t.pageBg, color: t.textMuted, borderRight: `1px solid ${t.borderFaint}` }}>$</span>
                      <input
                        type="number"
                        value={item.amount}
                        onChange={(e) => updateItem(item.id, 'amount', e.target.value)}
                        placeholder="0.00"
                        style={{ ...s.amountInput, width: '80px', background: t.inputBg, color: t.text }}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </td>

                  {/* Frequency */}
                  <td style={s.tableCell}>
                    <select
                      value={item.frequency}
                      onChange={(e) => updateItem(item.id, 'frequency', e.target.value)}
                      style={{ ...s.freqSelect, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
                    >
                      {FREQS.map((f) => (
                        <option key={f} value={f}>{FREQ_DISPLAY_LABELS[f]}</option>
                      ))}
                    </select>
                  </td>

                  {/* Date */}
                  <td style={s.tableCell}>
                    {item.frequency !== 'Daily' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <input
                          type="date"
                          value={item.lastOccurrence}
                          onChange={(e) => updateItem(item.id, 'lastOccurrence', e.target.value)}
                          max={TODAY}
                          style={{ ...s.dateInput, border: `1px solid ${t.borderFaint}`, color: t.text }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const now = new Date();
                            const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                            updateItem(item.id, 'lastOccurrence', firstOfMonth);
                          }}
                          style={{
                            background: 'none', border: 'none', padding: 0,
                            fontSize: '11px', color: t.textMuted, cursor: 'pointer',
                            textDecoration: 'underline', fontFamily: 'inherit',
                            textAlign: 'left', whiteSpace: 'nowrap',
                          }}
                        >
                          Not sure? Use the 1st
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: t.textFaintest }}>Every day</span>
                    )}
                  </td>

                  {/* Remove */}
                  <td style={{ ...s.tableCell, textAlign: 'center' }}>
                    <button onClick={() => removeItem(item.id)} style={s.removeBtn}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <hr style={{ ...s.divider, borderTop: `1px solid ${t.border}` }} />

      <div style={s.navRow}>
        <button style={{ ...s.backBtn, background: t.card, border: `1px solid ${t.borderFaint}`, color: t.textSecondary }} onClick={onBack}>
          ← Back
        </button>
        <button style={s.nextBtn} onClick={onNext}>
          Next →
        </button>
      </div>
    </div>
  );
}

// ─── Page 4: Overview ────────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function PageOverview({ userName, balance, incomeItems, billItems, onEdit, onBack, onFinish }) {
  const { t } = useTheme();
  const fmtAmt = (v) => (v && parseFloat(v) > 0 ? `$${parseFloat(v).toFixed(2)}` : '—');
  const totalIncome = incomeItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalBills = billItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ ...s.pageTitle, color: t.text }}>Your Month at a Glance</h2>
        <p style={{ ...s.pageSubtitle, color: t.textMuted }}>Review your setup — use the Edit buttons to make any changes.</p>
      </div>

      {/* Your info */}
      <OverviewSection title="Your Info" onEdit={() => onEdit(1)}>
        <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
          <div>
            <span style={{ ...s.ovLabel, color: t.textMuted }}>Name</span>
            <span style={{ ...s.ovValue, color: t.text }}>{userName || 'Not set'}</span>
          </div>
          <div>
            <span style={{ ...s.ovLabel, color: t.textMuted }}>Starting Balance</span>
            <span style={{ ...s.ovValue, color: t.text }}>{balance ? `$${parseFloat(balance).toFixed(2)}` : '—'}</span>
          </div>
        </div>
      </OverviewSection>

      {/* Income */}
      <OverviewSection
        title="Income Sources"
        badge={incomeItems.length > 0 ? `${incomeItems.length} added` : undefined}
        onEdit={() => onEdit(2)}
      >
        {incomeItems.length === 0 ? (
          <p style={{ ...s.ovEmpty, color: t.textFaintest }}>No income sources added</p>
        ) : (
          <>
            {incomeItems.map((item) => (
              <div key={item.id} style={{ ...s.ovRow, borderBottom: `1px solid ${t.borderLight}` }}>
                <span>{item.label === 'Other' ? item.customName || 'Other' : item.label}</span>
                <span style={{ color: '#1b6d24', fontWeight: '600', textAlign: 'right' }}>
                  {fmtAmt(item.amount)}{item.amount ? ` / ${item.frequency}` : ''}
                  {item.lastOccurrence ? <span style={{ display: 'block', fontSize: '11px', color: t.textMuted, fontWeight: '400' }}>last: {fmtDate(item.lastOccurrence)}</span> : null}
                </span>
              </div>
            ))}
          </>
        )}
      </OverviewSection>

      {/* Bills */}
      <OverviewSection
        title="Regular Bills"
        badge={billItems.length > 0 ? `${billItems.length} added` : undefined}
        onEdit={() => onEdit(3)}
      >
        {billItems.length === 0 ? (
          <p style={{ ...s.ovEmpty, color: t.textFaintest }}>No bills added</p>
        ) : (
          <>
            {billItems.map((item) => (
              <div key={item.id} style={{ ...s.ovRow, borderBottom: `1px solid ${t.borderLight}` }}>
                <span>{item.label === 'Other' ? item.customName || 'Other' : item.label}</span>
                <span style={{ color: '#ba1a1a', fontWeight: '600', textAlign: 'right' }}>
                  {fmtAmt(item.amount)}{item.amount ? ` / ${item.frequency}` : ''}
                  {item.lastOccurrence ? <span style={{ display: 'block', fontSize: '11px', color: t.textMuted, fontWeight: '400' }}>last: {fmtDate(item.lastOccurrence)}</span> : null}
                </span>
              </div>
            ))}
          </>
        )}
      </OverviewSection>

      {(totalIncome > 0 || totalBills > 0) && (
        <div style={{ ...s.card, background: t.card, marginBottom: '14px' }}>
          <div style={{ padding: '14px 16px', fontSize: '14px', color: t.textSecondary, lineHeight: '1.6' }}>
            {totalIncome > 0 && totalBills > 0 ? (
              <>You bring in about <strong style={{ color: '#1b6d24' }}>${totalIncome.toFixed(2)}/month</strong> and pay about <strong style={{ color: '#ba1a1a' }}>${totalBills.toFixed(2)}/month</strong>.</>
            ) : totalIncome > 0 ? (
              <>You bring in about <strong style={{ color: '#1b6d24' }}>${totalIncome.toFixed(2)}/month</strong>.</>
            ) : (
              <>Your monthly bills total about <strong style={{ color: '#ba1a1a' }}>${totalBills.toFixed(2)}/month</strong>.</>
            )}
          </div>
        </div>
      )}

      <MiniCalendarPreview balance={balance} incomeItems={incomeItems} billItems={billItems} />

      <hr style={{ ...s.divider, borderTop: `1px solid ${t.border}` }} />

      <div style={s.navRow}>
        <button style={{ ...s.backBtn, background: t.card, border: `1px solid ${t.borderFaint}`, color: t.textSecondary }} onClick={onBack}>
          ← Back
        </button>
        <button style={s.finishBtn} onClick={onFinish}>
          Finish Setup ✓
        </button>
      </div>
    </div>
  );
}

// ─── Mini Calendar Preview ───────────────────────────────────────────────────

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_ABBRS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function MiniCalendarPreview({ balance, incomeItems, billItems }) {
  const { t, isDark } = useTheme();
  const today = new Date();
  const year  = today.getFullYear();
  const month = today.getMonth();

  // Convert onboarding items → budgetItems shape expected by calendarMath
  const budgetItems = useMemo(() => [
    ...incomeItems.map(i => ({
      id: i.id,
      name: i.label === 'Other' ? (i.customName || 'Other') : i.label,
      amount: parseFloat(i.amount) || 0,
      frequency: i.frequency,
      lastOccurrence: i.lastOccurrence || null,
      type: 'income',
    })),
    ...billItems.map(i => ({
      id: i.id,
      name: i.label === 'Other' ? (i.customName || 'Other') : i.label,
      amount: parseFloat(i.amount) || 0,
      frequency: i.frequency,
      lastOccurrence: i.lastOccurrence || null,
      type: 'expense',
    })),
  ].filter(i => i.amount > 0), [incomeItems, billItems]);

  const startBalance  = parseFloat(balance) || 0;
  const daysInMonth   = getDaysInMonth(year, month);
  const firstDayOfWk  = getFirstDayOfMonth(year, month);

  // Balance for every day of the month
  const dayBalances = useMemo(() => {
    const result = {};
    for (let d = 1; d <= daysInMonth; d++) {
      result[d] = getDayBalance(year, month, d, startBalance, budgetItems);
    }
    return result;
  }, [year, month, daysInMonth, startBalance, budgetItems]);

  const endBalance       = dayBalances[daysInMonth];
  const firstNegDay      = Object.entries(dayBalances).find(([, v]) => v < 0);
  const hasAnyData       = budgetItems.length > 0 || startBalance !== 0;

  // Build cell array: leading nulls + day numbers
  const cells = [];
  for (let i = 0; i < firstDayOfWk; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const fmtCompact = (n) => {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1000) return sign + '$' + (abs / 1000).toFixed(1) + 'k';
    return sign + '$' + Math.round(abs);
  };

  if (!hasAnyData) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontWeight: '600', fontSize: '14px', color: t.textSub }}>
          {MONTH_NAMES[month]} Balance Preview
        </span>
      </div>

      <div style={{ ...s.card, background: t.card }}>
        <div style={{ padding: '12px 12px 8px' }}>
          {/* Day-of-week headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
            {DAY_ABBRS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '9px', fontWeight: '700', color: t.textMuted, letterSpacing: '0.03em', padding: '2px 0' }}>
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} />;
              const bal    = dayBalances[day];
              const isNeg  = bal < 0;
              const isToday = day === today.getDate();
              return (
                <div key={day} style={{
                  padding: '3px 1px 4px',
                  borderRadius: '5px',
                  background: isToday
                    ? (isDark ? '#07253f' : '#dbeeff')
                    : 'transparent',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '9px', color: t.textMuted, fontWeight: isToday ? '700' : '400', lineHeight: 1.3 }}>
                    {day}
                  </div>
                  <div style={{
                    fontSize: '8px', fontWeight: '600', lineHeight: 1.2,
                    color: isNeg ? '#ba1a1a' : '#1b6d24',
                  }}>
                    {fmtCompact(bal)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* End-of-month projection */}
        <div style={{
          borderTop: `1px solid ${t.borderLight}`,
          padding: '10px 14px',
          background: isDark ? 'rgba(0,97,164,0.08)' : '#f0f6ff',
          borderRadius: firstNegDay ? '0' : '0 0 10px 10px',
        }}>
          <div style={{ fontSize: '13px', color: t.textSecondary, lineHeight: '1.5' }}>
            By <strong>{MONTH_NAMES[month]} {daysInMonth}</strong>, your balance will be approximately{' '}
            <strong style={{ color: endBalance >= 0 ? '#1b6d24' : '#ba1a1a' }}>
              {endBalance < 0 ? '-' : ''}${Math.abs(endBalance).toFixed(2)}
            </strong>
          </div>
        </div>

        {/* Negative balance warning */}
        {firstNegDay && (
          <div style={{
            padding: '9px 14px',
            background: 'rgba(186,26,26,0.07)',
            borderTop: `1px solid rgba(186,26,26,0.15)`,
            borderRadius: '0 0 10px 10px',
          }}>
            <div style={{ fontSize: '12px', color: '#ba1a1a', fontWeight: '500' }}>
              ⚠️ Heads up — your balance is projected to drop below $0 on <strong>{MONTH_NAMES[month]} {firstNegDay[0]}</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OverviewSection({ title, badge, onEdit, children }) {
  const { t } = useTheme();
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: '600', fontSize: '14px', color: t.textSub }}>{title}</span>
          {badge && (
            <span style={{ fontSize: '12px', color: t.textMuted, background: t.borderLight, padding: '2px 8px', borderRadius: '10px' }}>
              {badge}
            </span>
          )}
        </div>
        <button onClick={onEdit} style={{ ...s.editBtn, background: t.card, border: `1px solid ${t.borderFaint}`, color: t.textSecondary }}>
          Edit
        </button>
      </div>
      <div style={{ ...s.card, background: t.card }}>
        <div style={{ padding: '12px 16px' }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = {
  page: {
    minHeight: '100vh',
    padding: '32px 20px 60px',
    display: 'flex',
    justifyContent: 'center',
  },
  container: {
    width: '100%',
    maxWidth: '660px',
  },
  stepBar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '32px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '700',
    margin: '0 0 6px',
    color: '#1a1a2e',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#777',
    margin: 0,
    lineHeight: '1.5',
  },
  card: {
    borderRadius: '10px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  fieldGroup: {
    padding: '20px',
  },
  cardDivider: {
    borderTop: '1px solid #f0f0f0',
  },
  label: {
    display: 'block',
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#1a1a2e',
  },
  textInput: {
    width: '100%',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '11px 14px',
    fontSize: '15px',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  hint: {
    fontSize: '13px',
    color: '#999',
    margin: '8px 0 0',
    lineHeight: '1.5',
    fontStyle: 'italic',
  },
  amountWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'white',
  },
  dollar: {
    padding: '0 10px',
    color: '#888',
    fontSize: '15px',
    borderRight: '1px solid #ddd',
    background: '#f9f9f9',
    lineHeight: '40px',
    userSelect: 'none',
  },
  amountInput: {
    border: 'none',
    outline: 'none',
    padding: '10px 12px',
    fontSize: '15px',
    fontFamily: 'inherit',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #e5e5e5',
    margin: '24px 0',
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skipBtn: {
    background: 'transparent',
    border: 'none',
    color: '#bbb',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
    textDecoration: 'underline',
  },
  backBtn: {
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '10px 22px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#555',
    fontFamily: 'inherit',
  },
  nextBtn: {
    background: '#0061a4',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 28px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '700',
    fontFamily: 'inherit',
  },
  finishBtn: {
    background: '#1b6d24',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 28px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '700',
    fontFamily: 'inherit',
  },
  chipsPool: {
    background: 'white',
    borderRadius: '10px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    padding: '14px 16px',
    marginBottom: '10px',
  },
  chipsLabel: {
    fontSize: '12px',
    color: '#999',
    margin: '0 0 10px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  chip: {
    background: '#eeeef0',
    border: 'none',
    borderRadius: '20px',
    padding: '7px 16px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#1a1c1d',
    fontFamily: 'inherit',
    transition: 'background 0.15s, opacity 0.15s',
    userSelect: 'none',
  },
  dropZone: {
    border: '2px dashed rgba(0,97,164,0.3)',
    borderRadius: '12px',
    minHeight: '130px',
    transition: 'border-color 0.15s, background 0.15s',
    overflow: 'hidden',
  },
  dropHint: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '130px',
    color: '#ccc',
    fontSize: '14px',
    gap: '8px',
  },
  tableHead: {
    padding: '8px 12px',
    textAlign: 'left',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#888',
    whiteSpace: 'nowrap',
  },
  tableCell: {
    padding: '10px 12px',
    verticalAlign: 'middle',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #f5f5f5',
    gap: '12px',
    flexWrap: 'wrap',
    background: 'white',
  },
  itemLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#222',
    minWidth: '110px',
    flex: '0 0 auto',
  },
  customNameInput: {
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '7px 10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    minWidth: '120px',
  },
  itemControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    flex: '1 1 auto',
    justifyContent: 'flex-end',
  },
  freqSelect: {
    border: '1px solid #ddd',
    borderRadius: '7px',
    padding: '7px 8px',
    fontSize: '13px',
    fontFamily: 'inherit',
    cursor: 'pointer',
    outline: 'none',
    background: 'white',
    color: '#333',
  },
  dateInput: {
    border: '1px solid #ddd',
    borderRadius: '7px',
    padding: '6px 8px',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    color: '#333',
    cursor: 'pointer',
  },
  removeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '20px',
    padding: '0 4px',
    fontFamily: 'inherit',
    lineHeight: 1,
    flexShrink: 0,
  },
  editBtn: {
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#555',
    fontFamily: 'inherit',
  },
  ovLabel: {
    display: 'block',
    fontSize: '11px',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '3px',
  },
  ovValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a1a2e',
    display: 'block',
  },
  ovRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 0',
    fontSize: '14px',
    borderBottom: '1px solid #fafafa',
  },
  ovEmpty: {
    color: '#bbb',
    fontSize: '13px',
    margin: '4px 0',
    fontStyle: 'italic',
  },
};
